import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import VanillaTilt from '../../utils/vanilla-tilt';
import criticalWinImg from '../../assets/critical_win_card.png';

/**
 * RPG Critical Victory Banner - Centered 3D Modal
 * Displays the exact high-fidelity neon Critical Win artwork centered in the viewport,
 * with a dark semi-transparent backdrop, a subtle, tight neon green edge shadow,
 * and buttery-smooth VanillaTilt physics.
 */
export function RpgCriticalWinBanner({
  title = 'CRITICAL WIN',
  amount = '19.20',
  multiplier = '1.96x',
  streak = 3,
  onClose,
  autoCloseMs = 6000,
}) {
  const [visible, setVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const tiltRef = useRef(null);

  useEffect(() => {
    const timerIn = setTimeout(() => setVisible(true), 30);
    let timerOut;
    if (autoCloseMs) {
      timerOut = setTimeout(() => {
        handleDismiss();
      }, autoCloseMs);
    }
    return () => {
      clearTimeout(timerIn);
      if (timerOut) clearTimeout(timerOut);
    };
  }, [autoCloseMs]);

  // Initialize VanillaTilt for buttery-smooth 3D physics tilt
  useEffect(() => {
    const node = tiltRef.current;
    if (node) {
      VanillaTilt.init(node, {
        max: 16,               // Max tilt rotation in degrees
        perspective: 1000,     // 3D perspective distance
        scale: 1.02,           // Subtle 3D lift on interaction
        speed: 900,            // Speed of enter/exit transition
        easing: 'cubic-bezier(.03,.98,.52,.99)', // Smooth spring easing
        gyroscope: true,       // Tilt with mobile device orientation
        gyroscopeMinAngleX: -45,
        gyroscopeMaxAngleX: 45,
        gyroscopeMinAngleY: -45,
        gyroscopeMaxAngleY: 45,
        reset: true,           // Springs back to (0, 0) on release
      });
    }

    return () => {
      if (node && node.vanillaTilt) {
        node.vanillaTilt.destroy();
      }
    };
  }, []);

  const handleDismiss = () => {
    if (isClosing) return;
    setIsClosing(true);
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 320);
  };

  // Touch event bridge to VanillaTilt for silky-smooth mobile touch drag
  const handleTouchStart = (e) => {
    if (e.target.closest('button')) return;
    const node = tiltRef.current;
    if (node && node.vanillaTilt && e.touches[0]) {
      node.vanillaTilt.onMouseEnter();
      node.vanillaTilt.onMouseMove({
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY,
      });
    }
  };

  const handleTouchMove = (e) => {
    const node = tiltRef.current;
    if (node && node.vanillaTilt && e.touches[0]) {
      node.vanillaTilt.onMouseMove({
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY,
      });
    }
  };

  const handleTouchEnd = () => {
    const node = tiltRef.current;
    if (node && node.vanillaTilt) {
      node.vanillaTilt.onMouseLeave();
    }
  };

  const modalContent = (
    <div
      onClick={handleDismiss}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.58)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        transition: 'opacity 0.28s ease',
        opacity: visible ? 1 : 0,
        cursor: 'pointer',
        overflow: 'hidden',
        perspective: '1200px',
        WebkitPerspective: '1200px',
        touchAction: 'none',
      }}
    >
      <style>{`
        @keyframes criticalCardEntrance {
          0% {
            transform: scale(0.65) translateY(40px);
            opacity: 0;
            filter: brightness(1.2) drop-shadow(0 0 15px rgba(16, 185, 129, 0.4));
          }
          65% {
            transform: scale(1.03) translateY(-4px);
            opacity: 1;
            filter: brightness(1.05) drop-shadow(0 0 12px rgba(16, 185, 129, 0.35));
          }
          100% {
            transform: scale(1) translateY(0);
            opacity: 1;
            filter: brightness(1) drop-shadow(0 15px 30px rgba(0, 0, 0, 0.75)) drop-shadow(0 0 8px rgba(16, 185, 129, 0.3));
          }
        }

        @keyframes cardHeartbeatPulse {
          0%, 100% {
            transform: scale(1) translateY(0);
            filter: drop-shadow(0 15px 30px rgba(0, 0, 0, 0.75)) drop-shadow(0 0 8px rgba(16, 185, 129, 0.25));
          }
          50% {
            transform: scale(1.02) translateY(-2px);
            filter: drop-shadow(0 18px 36px rgba(0, 0, 0, 0.85)) drop-shadow(0 0 14px rgba(16, 185, 129, 0.4));
          }
        }

        @keyframes buttonShineHover {
          0% {
            box-shadow: 0 0 15px rgba(52, 211, 153, 0.4), inset 0 0 10px rgba(255, 255, 255, 0.2);
          }
          50% {
            box-shadow: 0 0 30px rgba(52, 211, 153, 0.8), inset 0 0 18px rgba(255, 255, 255, 0.4);
          }
          100% {
            box-shadow: 0 0 15px rgba(52, 211, 153, 0.4), inset 0 0 10px rgba(255, 255, 255, 0.2);
          }
        }

        .crit-btn-continue {
          transition: transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }
        .crit-btn-continue:hover {
          transform: scale(1.02);
          background: rgba(52, 211, 153, 0.12) !important;
          animation: buttonShineHover 1.5s infinite;
        }
        .crit-btn-continue:active {
          transform: scale(0.97) !important;
        }

        .crit-btn-close {
          transition: transform 0.15s ease, background-color 0.2s ease, box-shadow 0.2s ease;
        }
        .crit-btn-close:hover {
          transform: scale(1.12);
          background: rgba(255, 255, 255, 0.15) !important;
          box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
        }
        .crit-btn-close:active {
          transform: scale(0.92);
        }
      `}</style>

      {/* Entrance Animation Wrapper (Isolated from VanillaTilt) */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'min(90vw, 420px)',
          maxWidth: '420px',
          animation: 'criticalCardEntrance 0.38s cubic-bezier(0.16, 1, 0.3, 1) forwards',
          transformOrigin: 'center center',
          userSelect: 'none',
          cursor: 'default',
        }}
      >
        {/* BUTTERY-SMOOTH VANILLATILT CARD ELEMENT */}
        <div
          ref={tiltRef}
          style={{
            position: 'relative',
            width: '100%',
            transformStyle: 'preserve-3d',
            WebkitTransformStyle: 'preserve-3d',
            cursor: 'grab',
            touchAction: 'none',
            zIndex: 1,
          }}
        >
          {/* Dynamic Heartbeat Breathing Card Body */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              animation: 'cardHeartbeatPulse 6.0s ease-in-out infinite',
              transformOrigin: 'center center',
            }}
          >
            {/* Exact Artwork Image */}
            <img
              src={criticalWinImg}
              alt="Critical Win"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                pointerEvents: 'none',
              }}
            />

            {/* Interactive Close Hitbox (X Button in Top-Right) */}
            <button
              className="crit-btn-close"
              onClick={handleDismiss}
              title="Close"
              style={{
                position: 'absolute',
                top: '16.2%',
                right: '9.2%',
                width: '8.8%',
                height: '8.3%',
                borderRadius: '50%',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                outline: 'none',
                zIndex: 10,
              }}
              aria-label="Close Critical Win Banner"
            />

            {/* Interactive Continue Hitbox (CONTINUE -> Button at Bottom) */}
            <button
              className="crit-btn-continue"
              onClick={handleDismiss}
              title="Continue"
              style={{
                position: 'absolute',
                left: '11.0%',
                top: '71.4%',
                width: '78.6%',
                height: '15.4%',
                borderRadius: '35px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                outline: 'none',
                zIndex: 10,
              }}
              aria-label="Continue"
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalContent, document.body);
}
